import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { PageHero } from '@/components/shared/PageHero';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { storage, formatDateTime } from '@/lib/storage';
import { Search, Clipboard, AlertCircle, MapPin, FileText, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const TrackRequest = () => {
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get('ref') || '');
  const [result, setResult] = useState(null);
  const [searched, setSearched] = useState(false);

  const lookup = (id) => {
    if (!id?.trim()) return;
    const requests = storage.get('requests', []);
    const concerns = storage.get('concerns', []);
    const all = [
      ...requests.map((r) => ({ ...r, kind: 'request' })),
      ...concerns.map((c) => ({ ...c, kind: 'concern' })),
    ];
    const found = all.find((x) => x.id.toLowerCase() === id.trim().toLowerCase());
    setResult(found || null);
    setSearched(true);
    if (!found) toast.error('Reference number not found.');
  };

  useEffect(() => {
    const initial = params.get('ref');
    if (initial) lookup(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="track-request-page">
      <PageHero
        eyebrow="E-Services"
        title="Track Your Request"
        subtitle="Enter your reference number to check the status of a document request or concern report."
        breadcrumbs={[{ label: 'E-Services', to: '/e-services' }, { label: 'Track Request' }]}
      >
        <form
          onSubmit={(e) => { e.preventDefault(); lookup(ref); }}
          className="flex flex-col sm:flex-row gap-3 max-w-xl"
        >
          <div className="relative flex-1">
            <Clipboard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary-foreground/70" />
            <Input
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder="e.g., R-261128-AB12C or C-261128-XY9PQ"
              className="pl-9 bg-white/10 border-white/30 placeholder:text-primary-foreground/70 text-primary-foreground focus-visible:ring-white/40"
              data-testid="track-input"
            />
          </div>
          <Button type="submit" size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="track-submit">
            <Search className="mr-2 h-4 w-4" /> Track
          </Button>
        </form>
      </PageHero>

      <section className="container-app section-padding max-w-3xl">
        {searched && !result && (
          <Card className="p-8 text-center border-dashed">
            <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground" />
            <h3 className="mt-3 font-heading font-semibold">Reference not found</h3>
            <p className="mt-1 text-sm text-muted-foreground">Double-check your reference number and try again.</p>
          </Card>
        )}

        {result && (
          <Card className="overflow-hidden border-0 shadow-md-soft" data-testid="track-result">
            <div className="bg-gradient-hero text-primary-foreground p-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] opacity-80">Reference</div>
                  <div className="font-display text-2xl font-bold">{result.id}</div>
                </div>
                <Badge className="bg-white/15 text-primary-foreground hover:bg-white/25 border-white/20">
                  {result.kind === 'request' ? 'Document Request' : 'Concern Report'}
                </Badge>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <DetailRow icon={FileText} label={result.kind === 'request' ? 'Service' : 'Category'} value={result.type || result.category} />
                <DetailRow icon={Calendar} label="Submitted" value={formatDateTime(result.submittedAt)} />
                {result.purok || result.address || result.location ? (
                  <DetailRow
                    icon={MapPin}
                    label="Area"
                    value={result.purok || 'Barangay San Isidro'}
                  />
                ) : null}
                <DetailRow label="Current Status" value={<StatusBadge status={result.status} />} />
              </div>
              <p className="text-xs text-muted-foreground">
                For privacy and safety, only limited case details are shown online.
              </p>

              {result.timeline && (
                <div>
                  <h4 className="font-heading font-semibold mb-3">Timeline</h4>
                  <ol className="relative border-l-2 border-border pl-6 space-y-4">
                    {result.timeline.map((t, i) => (
                      <li key={i} className="relative">
                        <span className="absolute -left-[31px] top-1 h-4 w-4 rounded-full bg-primary ring-4 ring-background" />
                        <div className="text-sm font-semibold">{t.status}</div>
                        <div className="text-xs text-muted-foreground">{formatDateTime(t.date)}</div>
                        {t.note && <div className="mt-1 text-sm text-muted-foreground">{t.note}</div>}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </Card>
        )}

        {!searched && (
          <Card className="p-8 text-center bg-secondary/40 border-dashed">
            <Search className="h-7 w-7 mx-auto text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">Enter your reference number above to track the status of your request or concern.</p>
          </Card>
        )}
      </section>
    </div>
  );
};

const DetailRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    {Icon && <Icon className="h-4 w-4 mt-0.5 text-primary" />}
    <div>
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-sm font-medium mt-0.5">{value || '—'}</div>
    </div>
  </div>
);

export default TrackRequest;
